import type { ReactNode } from 'react';
import { useQueuePulse } from './lib/useQueuePulse';

/**
 * One column of the operations-panel's side-by-side protected queues
 * (control-panel spec §2). It wraps a queue (the Urgent Alert queue or the ASSIST
 * queue) in a bounded, scrollable box and gives its header a subtle one-shot
 * PULSE when that queue updates — so an operator notices a new item with both
 * queues visible (no hidden tab to blink, since side-by-side is now the sole
 * portrait layout).
 *
 * The pulse is driven by the queue's signal counter (`useQueuePulse`); a top
 * accent bar replays a finite CSS fade on each tick (keyed by `pulseKey` so it
 * remounts and re-animates without remounting the queue beneath — its scroll
 * position and polling survive). Colour follows Colour Law: alert = URGENT-RED,
 * assist = steadier violet; `prefers-reduced-motion` swaps the fade for a static
 * tint (see `index.css`).
 */
export function PanelQueueColumn({
  tab,
  count,
  children,
}: {
  tab: 'alert' | 'assist';
  /** Monotonic update counter for this queue (from `useQueueSignals`). */
  count: number;
  children: ReactNode;
}): JSX.Element {
  const pulseKey = useQueuePulse(count);

  // The Urgent Alert queue (AnomalyQueuePanel) has no border of its own, so the
  // column supplies one; the ASSIST queue (AssistQueueList) brings its own border
  // — don't double it.
  const border = tab === 'alert' ? 'border border-slate-700' : '';

  return (
    <div
      data-testid={`panel-queue-col-${tab}`}
      data-pulse-count={pulseKey}
      className={`relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg ${border}`}
    >
      {pulseKey > 0 && (
        <div
          // Remount on every tick so the finite fade replays without touching the
          // queue beneath it.
          key={pulseKey}
          data-testid={`panel-queue-pulse-${tab}`}
          aria-hidden
          className={`pointer-events-none absolute inset-x-0 top-0 z-10 h-1 ${
            tab === 'alert' ? 'panel-queue-pulse-alert' : 'panel-queue-pulse-assist'
          }`}
        />
      )}
      {children}
    </div>
  );
}
